/**
 * Unified Notification Template Manager — shadcn/Tailwind
 * Context-aware template manager (enterprise/tenant) with CRUD, test, preview
 */

import React, { useState, useEffect } from 'react';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Switch } from '../../components/ui/switch';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../../components/ui/alert-dialog';
import { toast } from '../../components/ui/use-toast';
import { Bell, Plus, Pencil, Trash2, Send, Eye, Copy, Mail, MessageSquare, Phone, Info } from 'lucide-react';
import { subdomainDetector } from '../../utils/subdomainDetector';
import { PageHeader } from '../ui/page-header';
import { rowOpenProps } from '@/lib/rowOpen';

interface NotificationTemplate {
  id: number; name: string; type: string; subject?: string; content: string;
  channel: 'email' | 'sms' | 'whatsapp' | 'push'; is_active: boolean;
  is_system_template: boolean; variables: string[]; priority: 'low' | 'medium' | 'high' | 'urgent';
  usage_count: number; created_at: string; updated_at: string;
}

interface NotificationTemplateStats {
  total_templates: number; active_templates: number; system_templates: number; custom_templates: number;
}

const channelIcons: Record<string, React.ReactNode> = {
  email: <Mail className="h-3 w-3" />, sms: <MessageSquare className="h-3 w-3" />,
  whatsapp: <Phone className="h-3 w-3" />, push: <Bell className="h-3 w-3" />,
};

const channelColors: Record<string, string> = {
  email: 'bg-info-100 text-info-700', sms: 'bg-success-100 text-success-700',
  whatsapp: 'bg-info-100 text-info-700', push: 'bg-info-100 text-info-700',
};

const priorityColors: Record<string, string> = {
  low: 'bg-muted text-foreground', medium: 'bg-info-100 text-info-700',
  high: 'bg-warning-100 text-warning-700', urgent: 'bg-destructive-100 text-destructive-700',
};

const UnifiedNotificationTemplateManager: React.FC = () => {
  const isEnterprise = subdomainDetector.isEnterprise();
  const contextLabel = isEnterprise ? 'Enterprise Platform' : 'Tenant Organization';
  const apiBasePath = isEnterprise
    ? `${subdomainDetector.getApiBaseUrl()}/v1/platform/notifications/templates`
    : `${subdomainDetector.getApiBaseUrl()}/v1/core/api/notifications/templates`;

  const [templates, setTemplates] = useState<NotificationTemplate[]>([]);
  const [stats, setStats] = useState<NotificationTemplateStats>({ total_templates: 0, active_templates: 0, system_templates: 0, custom_templates: 0 });
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [testModalVisible, setTestModalVisible] = useState(false);
  const [previewModalVisible, setPreviewModalVisible] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState<NotificationTemplate | null>(null);
  const [selectedTemplate, setSelectedTemplate] = useState<NotificationTemplate | null>(null);
  const [deleteId, setDeleteId] = useState<number | null>(null);

  const [formData, setFormData] = useState({ name: '', type: '', channel: 'email', priority: 'medium', subject: '', content: '', is_active: true });
  const [testRecipient, setTestRecipient] = useState('');

  const fetchTemplates = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${apiBasePath}/`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('jwt_access_token')}`, 'Content-Type': 'application/json' },
      });
      if (response.ok) { const data = await response.json(); setTemplates(data.results || data); }
      else toast({ title: `Failed to load ${contextLabel.toLowerCase()} templates`, variant: 'destructive' });
    } catch { toast({ title: `Error loading ${contextLabel.toLowerCase()} templates`, variant: 'destructive' }); }
    finally { setLoading(false); }
  };

  const fetchStats = async () => {
    try {
      const endpoint = isEnterprise ? 'statistics' : 'stats';
      const response = await fetch(`${apiBasePath}/${endpoint}/`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('jwt_access_token')}`, 'Content-Type': 'application/json' },
      });
      if (response.ok) setStats(await response.json());
    } catch { /* ignore */ }
  };

  useEffect(() => { fetchTemplates(); fetchStats(); }, []);

  const handleAdd = () => { setEditingTemplate(null); setFormData({ name: '', type: '', channel: 'email', priority: 'medium', subject: '', content: '', is_active: true }); setModalVisible(true); };

  const handleEdit = (t: NotificationTemplate) => {
    setEditingTemplate(t);
    setFormData({ name: t.name, type: t.type, channel: t.channel, priority: t.priority, subject: t.subject || '', content: t.content, is_active: t.is_active });
    setModalVisible(true);
  };

  const handleSave = async () => {
    setLoading(true);
    try {
      const method = editingTemplate ? 'PUT' : 'POST';
      const url = editingTemplate ? `${apiBasePath}/${editingTemplate.id}/` : `${apiBasePath}/`;
      const response = await fetch(url, {
        method, headers: { Authorization: `Bearer ${localStorage.getItem('jwt_access_token')}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(formData),
      });
      if (response.ok) { toast({ title: `Template ${editingTemplate ? 'updated' : 'created'}`, variant: 'success' }); setModalVisible(false); fetchTemplates(); fetchStats(); }
      else toast({ title: `Failed to ${editingTemplate ? 'update' : 'create'} template`, variant: 'destructive' });
    } catch { toast({ title: 'Error saving template', variant: 'destructive' }); }
    finally { setLoading(false); }
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    setLoading(true);
    try {
      const response = await fetch(`${apiBasePath}/${deleteId}/`, {
        method: 'DELETE', headers: { Authorization: `Bearer ${localStorage.getItem('jwt_access_token')}` },
      });
      if (response.ok) { toast({ title: 'Template deleted', variant: 'success' }); setDeleteId(null); fetchTemplates(); fetchStats(); }
      else toast({ title: 'Failed to delete template', variant: 'destructive' });
    } catch { toast({ title: 'Error deleting template', variant: 'destructive' }); }
    finally { setLoading(false); }
  };

  const handleDuplicate = async (t: NotificationTemplate) => {
    setLoading(true);
    try {
      const response = await fetch(`${apiBasePath}/`, {
        method: 'POST', headers: { Authorization: `Bearer ${localStorage.getItem('jwt_access_token')}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...t, name: `${t.name} (Copy)`, id: undefined }),
      });
      if (response.ok) { toast({ title: 'Template duplicated', variant: 'success' }); fetchTemplates(); fetchStats(); }
      else toast({ title: 'Failed to duplicate template', variant: 'destructive' });
    } catch { toast({ title: 'Error duplicating template', variant: 'destructive' }); }
    finally { setLoading(false); }
  };

  const handleTest = (t: NotificationTemplate) => { setSelectedTemplate(t); setTestRecipient(''); setTestModalVisible(true); };

  const handleSendTest = async () => {
    if (!selectedTemplate) return;
    setLoading(true);
    try {
      const response = await fetch(`${apiBasePath}/${selectedTemplate.id}/send_test/`, {
        method: 'POST', headers: { Authorization: `Bearer ${localStorage.getItem('jwt_access_token')}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ recipient: testRecipient }),
      });
      if (response.ok) { toast({ title: `Test ${selectedTemplate.channel} sent`, variant: 'success' }); setTestModalVisible(false); }
      else toast({ title: `Failed to send test ${selectedTemplate.channel}`, variant: 'destructive' });
    } catch { toast({ title: 'Error sending test', variant: 'destructive' }); }
    finally { setLoading(false); }
  };

  const handlePreview = (t: NotificationTemplate) => { setSelectedTemplate(t); setPreviewModalVisible(true); };

  const usageRate = stats.total_templates ? Math.round((stats.active_templates / stats.total_templates) * 100) : 0;

  return (
    <div className="space-y-6">
      <PageHeader
        title={`${contextLabel} Notification Templates`}
        icon={<Bell className="h-6 w-6" />}
        description={`Manage notification templates for ${isEnterprise ? 'enterprise platform communications' : 'laboratory operations and patient communications'}.`}
      />

      <div className="bg-info-100 border border-info-200 rounded-md p-3 flex items-center gap-2">
        <Info className="h-4 w-4 text-info" />
        <p className="text-sm text-foreground">{contextLabel} Mode — Templates saved to {isEnterprise ? 'enterprise platform' : 'tenant organization'} database.</p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-4 gap-4">
        {[['Total Templates', stats.total_templates], ['Active Templates', stats.active_templates], ['System Templates', stats.system_templates], ['Usage Rate', `${usageRate}%`]].map(([label, val]) => (
          <Card key={label as string} className="p-4"><p className="text-sm text-muted-foreground">{label as string}</p><p className="text-2xl font-bold">{val}</p></Card>
        ))}
      </div>

      {/* Table */}
      <Card>
        <div className="p-4 border-b flex items-center justify-between">
          <h3 className="font-semibold">{contextLabel} Notification Templates</h3>
          <Button onClick={handleAdd}><Plus className="h-4 w-4 mr-1" />Add Template</Button>
        </div>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Template</TableHead><TableHead>Channel</TableHead><TableHead>Priority</TableHead>
              <TableHead>Status</TableHead><TableHead>Usage</TableHead><TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {templates.map((t) => (
              <TableRow key={t.id} {...rowOpenProps(() => handleEdit(t))}>
                <TableCell><div><span className="font-medium">{t.name}</span><br /><span className="text-xs text-muted-foreground">{t.type}</span></div></TableCell>
                <TableCell><Badge className={channelColors[t.channel]}>{channelIcons[t.channel]} <span className="ml-1">{t.channel?.toUpperCase()}</span></Badge></TableCell>
                <TableCell><Badge className={priorityColors[t.priority]}>{t.priority?.toUpperCase()}</Badge></TableCell>
                <TableCell><Badge variant={t.is_active ? 'default' : 'secondary'}>{t.is_active ? 'Active' : 'Inactive'}</Badge></TableCell>
                <TableCell>{t.usage_count?.toLocaleString()}</TableCell>
                <TableCell className="text-right">
                  <div className="flex justify-end gap-1">
                    <Button variant="ghost" size="sm" onClick={() => handleEdit(t)}><Pencil className="h-3 w-3" /></Button>
                    <Button variant="ghost" size="sm" onClick={() => handlePreview(t)}><Eye className="h-3 w-3" /></Button>
                    <Button variant="ghost" size="sm" onClick={() => handleTest(t)}><Send className="h-3 w-3" /></Button>
                    <Button variant="ghost" size="sm" onClick={() => handleDuplicate(t)}><Copy className="h-3 w-3" /></Button>
                    {!t.is_system_template && <Button variant="ghost" size="sm" onClick={() => setDeleteId(t.id)}><Trash2 className="h-3 w-3 text-destructive-700" /></Button>}
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>

      {/* Editor Modal */}
      <Dialog open={modalVisible} onOpenChange={setModalVisible}>
        <DialogContent className="max-w-3xl">
          <DialogHeader><DialogTitle>{editingTemplate ? 'Edit' : 'Add'} {contextLabel} Template</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2"><Label>Template Name *</Label><Input value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} /></div>
              <div className="space-y-2"><Label>Template Type *</Label>
                <Select value={formData.type} onValueChange={(v) => setFormData({ ...formData, type: v })}>
                  <SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent><SelectItem value="welcome">Welcome</SelectItem><SelectItem value="notification">Notification</SelectItem><SelectItem value="reminder">Reminder</SelectItem><SelectItem value="alert">Alert</SelectItem><SelectItem value="report">Report</SelectItem></SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2"><Label>Channel *</Label>
                <Select value={formData.channel} onValueChange={(v) => setFormData({ ...formData, channel: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="email">Email</SelectItem><SelectItem value="sms">SMS</SelectItem><SelectItem value="whatsapp">WhatsApp</SelectItem><SelectItem value="push">Push Notification</SelectItem></SelectContent>
                </Select>
              </div>
              <div className="space-y-2"><Label>Priority *</Label>
                <Select value={formData.priority} onValueChange={(v) => setFormData({ ...formData, priority: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="low">Low</SelectItem><SelectItem value="medium">Medium</SelectItem><SelectItem value="high">High</SelectItem><SelectItem value="urgent">Urgent</SelectItem></SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-2"><Label>Subject (Email only)</Label><Input value={formData.subject} onChange={(e) => setFormData({ ...formData, subject: e.target.value })} /></div>
            <div className="space-y-2"><Label>Content *</Label><Textarea rows={6} placeholder="Use {{variable_name}} for dynamic content." value={formData.content} onChange={(e) => setFormData({ ...formData, content: e.target.value })} /></div>
            <div className="flex items-center gap-2"><Switch checked={formData.is_active} onCheckedChange={(v) => setFormData({ ...formData, is_active: v })} /><Label>Active</Label></div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setModalVisible(false)}>Cancel</Button><Button onClick={handleSave} disabled={loading}>{editingTemplate ? 'Update' : 'Create'} Template</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Test Modal */}
      <Dialog open={testModalVisible} onOpenChange={setTestModalVisible}>
        <DialogContent>
          <DialogHeader><DialogTitle>Send Test {selectedTemplate?.channel?.toUpperCase()}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2"><Label>{selectedTemplate?.channel === 'email' ? 'Email Address' : 'Phone Number'} *</Label><Input placeholder={selectedTemplate?.channel === 'email' ? 'Enter email' : 'Enter phone number'} value={testRecipient} onChange={(e) => setTestRecipient(e.target.value)} /></div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setTestModalVisible(false)}>Cancel</Button><Button onClick={handleSendTest} disabled={loading}>Send Test</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Preview Modal */}
      <Dialog open={previewModalVisible} onOpenChange={setPreviewModalVisible}>
        <DialogContent>
          <DialogHeader><DialogTitle>Template Preview</DialogTitle></DialogHeader>
          {selectedTemplate && (
            <div className="space-y-4">
              <div><p className="font-medium text-sm">Name:</p><p>{selectedTemplate.name}</p></div>
              {selectedTemplate.subject && <div><p className="font-medium text-sm">Subject:</p><div className="bg-muted p-2 rounded text-sm">{selectedTemplate.subject}</div></div>}
              <div><p className="font-medium text-sm">Content:</p><div className="bg-muted p-3 rounded text-sm whitespace-pre-wrap">{selectedTemplate.content}</div></div>
              <div className="flex gap-2">
                <Badge className={channelColors[selectedTemplate.channel]}>{selectedTemplate.channel?.toUpperCase()}</Badge>
                <Badge className={priorityColors[selectedTemplate.priority]}>{selectedTemplate.priority?.toUpperCase()}</Badge>
                <Badge variant={selectedTemplate.is_active ? 'default' : 'secondary'}>{selectedTemplate.is_active ? 'Active' : 'Inactive'}</Badge>
              </div>
            </div>
          )}
          <DialogFooter><Button variant="outline" onClick={() => setPreviewModalVisible(false)}>Close</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirm */}
      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader><AlertDialogTitle>Delete Template?</AlertDialogTitle><AlertDialogDescription>This will permanently delete this notification template.</AlertDialogDescription></AlertDialogHeader>
          <AlertDialogFooter><AlertDialogCancel>Cancel</AlertDialogCancel><AlertDialogAction onClick={handleDelete}>Delete</AlertDialogAction></AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default UnifiedNotificationTemplateManager;
